import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useCache } from '../../context/CacheContext';
import { cachedPayerService } from '../../services/cachedApiService';
import { Insurance } from '../../types';
import '../../styles/EPAManagement.css';

// Interface for grouped insurance plans
interface GroupedInsurancePlan {
  id: number;  // ID of the first insurance record in the group
  plan_name: string;
  provider: string;
  payer_name: string;
  type: string;
  ePA: boolean;
  patient_count: number;
  patients: string[];  // List of patient names
}

const EPAManagement: React.FC = () => {
  const [insurancePlans, setInsurancePlans] = useState<GroupedInsurancePlan[]>([]);
  const [filteredPlans, setFilteredPlans] = useState<GroupedInsurancePlan[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [updatingPlan, setUpdatingPlan] = useState<number | null>(null);
  const { user } = useAuth();
  const { invalidate } = useCache();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || user.role !== 'payer') {
        setError('Only payers can access this page');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch insurance plans for this payer
        const rawPlans = await cachedPayerService.getInsurancePlans();
        
        // Group plans by plan_name
        const planGroups: { [key: string]: Insurance[] } = {};
        
        rawPlans.forEach(plan => {
          const key = plan.plan_name;
          if (!planGroups[key]) {
            planGroups[key] = [];
          }
          planGroups[key].push(plan);
        });
        
        // Convert grouped plans to GroupedInsurancePlan format
        const groupedPlans: GroupedInsurancePlan[] = Object.entries(planGroups).map(([planName, plans]) => {
          const firstPlan = plans[0];
          return {
            id: firstPlan.id,
            plan_name: planName,
            provider: firstPlan.provider,
            payer_name: firstPlan.payer_name,
            type: firstPlan.type,
            ePA: firstPlan.ePA,
            patient_count: plans.length,
            patients: plans.map(p => p.patient_name).filter(name => name) // Filter out empty names
          };
        });
        
        setInsurancePlans(groupedPlans);
        setFilteredPlans(groupedPlans);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching insurance plans:', err);
        setError('Failed to fetch insurance plans');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  const handleToggleEPA = async (insuranceId: number, currentStatus: boolean) => {
    try {
      setUpdatingPlan(insuranceId);
      setSuccessMessage(null);
      setError(null);
      
      // Update ePA status
      await cachedPayerService.updateEPAStatus(insuranceId, !currentStatus);
      
      // Invalidate related caches
      invalidate.insurance();
      
      // Update local state
      const updatedPlans = insurancePlans.map(plan =>
        plan.id === insuranceId ? { ...plan, ePA: !currentStatus } : plan
      );
      
      setInsurancePlans(updatedPlans);
      setFilteredPlans(
        searchTerm.trim() === ''
          ? updatedPlans
          : updatedPlans.filter(plan =>
              plan.plan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              plan.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
              plan.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
              plan.patients.some(patient =>
                patient.toLowerCase().includes(searchTerm.toLowerCase())
              )
            )
      );
      
      const updatedPlan = updatedPlans.find(p => p.id === insuranceId);
      if (updatedPlan) {
        setSuccessMessage(
          `ePA ${!currentStatus ? 'enabled' : 'disabled'} for ${updatedPlan.plan_name} (affects ${updatedPlan.patient_count} patients)`
        );
      }
      setUpdatingPlan(null);
    } catch (err) {
      console.error('Error updating ePA status:', err);
      setError('Failed to update ePA status. Please try again.');
      setUpdatingPlan(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading insurance plans...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div>{error}</div>
          <div style={{ marginTop: '15px' }}>
            <button
              onClick={() => {
                invalidate.insurance();
                window.location.reload();
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3182ce',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <h2 className="page-title">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 15V17M12 7V13M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          ePA Management
        </h2>

        {successMessage && (
          <div className="alert alert-success fade-in">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22 11.0857V12.0057C21.9988 14.1621 21.3005 16.2604 20.0093 17.9875C18.7182 19.7147 16.9033 20.9782 14.8354 21.5896C12.7674 22.201 10.5573 22.1276 8.53447 21.3803C6.51168 20.633 4.78465 19.2518 3.61096 17.4428C2.43727 15.6338 1.87979 13.4938 2.02168 11.342C2.16356 9.19029 2.99721 7.14205 4.39828 5.5028C5.79935 3.86354 7.69279 2.72111 9.79619 2.24587C11.8996 1.77063 14.1003 1.98806 16.07 2.86572" stroke="currentColor" strokeWidth="2" />
              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" />
            </svg>
            {successMessage}
          </div>
        )}

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search by plan name, patient name, or type..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (value.trim() === '') {
                  setFilteredPlans(insurancePlans);
                } else {
                  setFilteredPlans(
                    insurancePlans.filter(plan =>
                      plan.plan_name.toLowerCase().includes(value.toLowerCase()) ||
                      plan.provider.toLowerCase().includes(value.toLowerCase()) ||
                      plan.type.toLowerCase().includes(value.toLowerCase()) ||
                      plan.patients.some(patient =>
                        patient.toLowerCase().includes(value.toLowerCase())
                      )
                    )
                  );
                }
              }}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilteredPlans(insurancePlans);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
          <div className="search-results-count">
            {filteredPlans.length} {filteredPlans.length === 1 ? 'plan' : 'plans'} found
          </div>
        </div>
        
        <div className="epa-card">
          <h3 className="card-title">Insurance Plans</h3>
          
          {filteredPlans.length > 0 ? (
            <ul className="insurance-list">
              {filteredPlans.map((plan) => (
                <li key={plan.id} className="list-item">
                  <div className="item-content">
                    <div className="plan-name">{plan.payer_name}</div>
                    
                    <div className="info-container">
                      <div className="info-left">
                        <div className="info-row">
                          <strong>Patients:</strong>
                          <span>{plan.patient_count} {plan.patient_count === 1 ? 'patient' : 'patients'}</span>
                        </div>
                        
                        <div className="info-row">
                          <strong>Type:</strong>
                          <span>{plan.type}</span>
                        </div>
                      </div>
                      
                      <div className="info-right">
                        <div className="info-row">
                          <strong>Provider:</strong>
                          <span>{plan.provider}</span>
                        </div>
                        
                        <div className="info-row">
                          <strong>ePA:</strong>
                          <span className={`epastatus ${plan.ePA ? 'available' : 'not-available'}`}>
                            {plan.ePA ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    className={`epa-button ${plan.ePA ? 'danger' : 'success'}`}
                    onClick={() => handleToggleEPA(plan.id, plan.ePA)}
                    disabled={updatingPlan === plan.id}
                  >
                    {updatingPlan === plan.id ? (
                      <span className="spinner"></span>
                    ) : plan.ePA ? (
                      'Disable ePA'
                    ) : (
                      'Enable ePA'
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="empty-state">
              <div className="empty-title">
                {insurancePlans.length > 0
                  ? `No plans found matching "${searchTerm}"`
                  : "No insurance plans found"}
              </div>
              <p className="empty-subtitle">
                {insurancePlans.length > 0
                  ? "Try a different search term or clear the search"
                  : "Add insurance plans to enable ePA"}
              </p>
            </div>
          )}
        </div>

        <div className="epa-card about-epa">
          <h3 className="card-title">About ePA</h3>
          <p className="about-description">
            Electronic Prior Authorization (ePA) allows healthcare providers to submit and receive prior authorization
            requests electronically, streamlining the process and reducing administrative burden.
          </p>
          <ul className="benefits-list">
            <li>Faster turnaround times for authorization decisions</li>
            <li>Reduced administrative costs for both providers and payers</li>
            <li>Improved patient satisfaction with quicker access to care</li>
            <li>Decreased phone calls and faxes for authorization status</li>
          </ul>
          <p className="about-footer">
            Enable ePA for your insurance plans to provide a better experience for providers and patients.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default EPAManagement;